import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Form, Dropdown, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import axios from 'axios';

const Products = ({
  setActiveView,                 // may be undefined
  setSelectedProduct,            // may be undefined
  selectedCategory: selectedCategoryProp,            // may be undefined
  setSelectedCategory: setSelectedCategoryProp,      // may be undefined
}) => {
  // --- Safe wrappers for optional callbacks ---
  const safeSetActiveView = useMemo(() => 
    typeof setActiveView === 'function' ? setActiveView : () => {}, [setActiveView]);
  const safeSetSelectedProduct =
    typeof setSelectedProduct === 'function' ? setSelectedProduct : () => {};

  // --- Local fallback for selectedCategory if parent doesn't control it ---
  const [localCategory, setLocalCategory] = useState(selectedCategoryProp ?? '');
  useEffect(() => {
    if (typeof selectedCategoryProp !== 'undefined') {
      setLocalCategory(selectedCategoryProp ?? '');
    }
  }, [selectedCategoryProp]);

  const category =
    (typeof selectedCategoryProp !== 'undefined' ? selectedCategoryProp : localCategory) || '';

  const setCategory =
    typeof setSelectedCategoryProp === 'function' ? setSelectedCategoryProp : setLocalCategory;

  // --- Rest of state ---
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const categoriesRes = await axios.get('/categories/getAllCategories', { withCredentials: true });
      const cats = Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.data || [];
      setCategories(cats);

      // Set first category as default if no category is selected and categories exist
      let currentCategory = category;
      if (!category && cats.length > 0) {
        currentCategory = cats[0].id;
        setCategory(currentCategory);
      }

      if (currentCategory) {
        const productsRes = await axios.post(
          '/products/by-category',
          { category: currentCategory },
          { withCredentials: true }
        );
        const list = Array.isArray(productsRes.data) ? productsRes.data : productsRes.data?.data || [];
        setProducts(list);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [category, setCategory]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchData();
    safeSetActiveView('products');
  }, [isAuthenticated, category, fetchData, safeSetActiveView]); // refetch when the effective category changes

  const handleAddProduct = () => {
    safeSetActiveView('addNewProduct');
    navigate('/dashboard/products/add');
  };

  const handleProductClick = (productId) => {
    // Safe: won't throw if prop is missing
    safeSetSelectedProduct(productId);
    safeSetActiveView('productDetail');

    // Also pass productId via route state so the detail page can read it even without the setter
    navigate(`/dashboard/products/${productId}`, { state: { productId } });
  };

  if (!isAuthenticated) return null;

  return (
    <div className="content-container">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Form.Label className="me-2 mb-0">Categories:</Form.Label>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary">
              {category
                ? categories.find((c) => c.id === category)?.name || 'Select Category'
                : 'Select Category'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {categories.map((c) => (
                <Dropdown.Item
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  active={category === c.id}
                >
                  {c.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {(user?.userGroup === 0 || user?.userGroup === 1) && (
          <Button variant="primary" onClick={handleAddProduct}>
            Add New Product
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="product-grid-container">
          {products.length > 0 ? (
            <Row className="g-4">
              {products.map((product) => (
                <Col key={product.id} xs={6} sm={4} md={3} lg={2} xl={2} xxl={2}>
                  <Card
                    className="h-100 product-card"
                    onClick={() => handleProductClick(product.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="d-flex flex-column align-items-center">
                      <Card.Title className="text-center mb-3">
                        {product.name}
                      </Card.Title>
                      <div className="product-image-container">
                        <img
                          src={product.imageURL || '/placeholder-product.png'}
                          alt={product.name}
                          className="product-image img-fluid"
                          style={{ maxHeight: '150px', maxWidth: '100%' }}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-5">
              {category ? 'No products found in this category' : 'Please select a category'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Products;